<script setup lang="ts">
/**
 * Full-screen mobile overlay for a service category detail view.
 *
 * Rendered as a fixed-position layer (z-index 10, above MobileBottomNav's 5)
 * via Teleport. iOS-style navbar provides back navigation and an Edit button.
 *
 * Category and service type data are pulled from the shared
 * useInfiniteQuery(['businessServiceCategories']) and ['businessServiceTypes']
 * caches (auto-drained, same keys as ServiceTypesPane).
 *
 * Edit opens AddServiceCategoryFormMobileScreen via the Pinia store. App.vue
 * renders that form (z-index 11) when addServiceCategoryFormStore.isOpen &&
 * isMobile, covering this screen until the user saves or cancels.
 *
 * Edit is intentionally called without clearInfoViewSelections because that
 * helper also clears businessServiceNameCardStore.selectedBusinessServiceNameId,
 * which would dismiss this screen before the form can overlay it.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import {
  fetchBusinessServiceCategories,
  deleteBusinessServiceCategory,
} from '@/api/businessServiceCategories'
import {
  fetchBusinessServiceTypes,
  businessServiceTypePriceAmount,
  type BusinessServiceType,
} from '@/api/businessServiceTypes'
import { serviceCategoriesPollingInterval, serviceTypesPollingInterval } from '@/config/api'
import { formatDuration } from '@/utils/formatDuration'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'
import { useAddServiceCategoryFormStore } from '@/stores/add_service_category_form_store'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  categoryId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesDelete))

// ── Category data ─────────────────────────────────────────────────────────────
const categoriesQuery = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceCategoriesPollingInterval,
  refetchInterval: serviceCategoriesPollingInterval,
  refetchIntervalInBackground: false,
})

watch(
  () => ({
    hasNext: categoriesQuery.hasNextPage.value,
    fetching: categoriesQuery.isFetchingNextPage.value,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void categoriesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

const category = computed(() => {
  const id = props.categoryId
  for (const page of categoriesQuery.data.value?.pages ?? []) {
    const found = (page.items ?? []).find((c) => c.id === id)
    if (found) return found
  }
  return null
})

// ── Service types for this category ──────────────────────────────────────────
const typesQuery = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceTypesPollingInterval,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
})

watch(
  () => ({
    hasNext: typesQuery.hasNextPage.value,
    fetching: typesQuery.isFetchingNextPage.value,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void typesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

const categoryTypes = computed<BusinessServiceType[]>(() => {
  const id = props.categoryId
  for (const page of typesQuery.data.value?.pages ?? []) {
    const group = (page.items ?? []).find((g) => g.business_service_name_id === id)
    if (group) return group.business_service_types
  }
  return []
})

const typeCount = computed(() => categoryTypes.value.length)

const avgPriceLabel = computed<string>(() => {
  const types = categoryTypes.value
  if (!types.length) return '—'
  const amounts = types
    .map((type) => businessServiceTypePriceAmount(type))
    .filter((v): v is number => v !== undefined)
  if (!amounts.length) return '—'
  const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length
  return formatMoney({ amount: avg, precision: 0, fractionDigits: 2 }).formatted
})

function typePrice(type: BusinessServiceType): string | null {
  const raw = businessServiceTypePriceAmount(type)
  if (raw === undefined) return null
  return formatMoney({ amount: raw, precision: 0, fractionDigits: 2 }).formatted
}

// ── Edit ──────────────────────────────────────────────────────────────────────
const addServiceCategoryFormStore = useAddServiceCategoryFormStore()

function openEdit(): void {
  const c = category.value
  if (!c) return
  addServiceCategoryFormStore.openForEdit(c.id, {
    name: c.name,
    description: c.description,
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteBusinessServiceCategory(props.categoryId),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['businessServiceCategories'] })
    void queryClient.invalidateQueries({ queryKey: ['businessServiceTypes'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() => {
  const c = category.value
  const name = c?.name ?? t('cards.serviceCategory.defaultCategoryName')
  return t('cards.serviceCategory.confirmDeleteDescriptionWithTypes', {
    name,
    count: typeCount.value,
  })
})

function handleDelete(): void {
  if (!category.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="scm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="scm-navbar">
        <button type="button" class="scm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('nav.categories') }}
        </button>
        <span class="scm-nav-title">{{ t('cards.serviceCategory.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="scm-nav-edit"
          :disabled="!category"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <!-- Loading / error -->
      <div v-if="categoriesQuery.isPending.value && !category" class="scm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div
        v-else-if="categoriesQuery.isError.value && !category"
        class="scm-state scm-state--error"
      >
        {{ t('cards.serviceCategory.loadErrorShort') }}
      </div>

      <!-- Content -->
      <div v-else-if="category" class="scm-scroll">
        <!-- Category header: name + description -->
        <div class="scm-cat-header">
          <div class="scm-cat-name">{{ category.name }}</div>
          <div v-if="category.description" class="scm-cat-desc">{{ category.description }}</div>
          <div v-else class="scm-cat-desc scm-cat-desc--empty">
            {{ t('cards.serviceCategory.noDescription') }}
          </div>
        </div>

        <!-- Stat strip -->
        <div class="scm-stat-strip">
          <div class="scm-stat-cell">
            <div class="scm-stat-num">{{ typeCount }}</div>
            <div class="scm-stat-cap">{{ t('tabs.serviceTypes.title') }}</div>
          </div>
          <div class="scm-stat-cell">
            <div class="scm-stat-num scm-stat-num--pos">{{ avgPriceLabel }}</div>
            <div class="scm-stat-cap">
              {{ t('cards.serviceCategory.avgPrice') }} {{ currencySymbol }}
            </div>
          </div>
        </div>

        <!-- Service types list -->
        <div class="scm-section-label">{{ t('cards.serviceCategory.serviceTypesInCategory') }}</div>
        <div class="scm-group">
          <template v-if="categoryTypes.length > 0">
            <div v-for="type in categoryTypes" :key="type.id" class="scm-type-row">
              <div class="scm-type-main">
                <div class="scm-type-name">{{ type.description || '—' }}</div>
                <div v-if="formatDuration(type.duration)" class="scm-type-sub">
                  {{ formatDuration(type.duration) }}
                </div>
              </div>
              <div v-if="typePrice(type)" class="scm-type-price">
                {{ typePrice(type) }}<span class="scm-type-cur">{{ currencySymbol }}</span>
              </div>
              <svg
                class="scm-type-chev"
                width="8"
                height="14"
                viewBox="0 0 8 14"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M1 1L7 7L1 13" />
              </svg>
            </div>
          </template>
          <div v-else class="scm-type-empty">
            {{ t('cards.serviceCategory.noServiceTypesYet') }}
          </div>
        </div>

        <!-- Grows to push Delete to the bottom of the screen when content is
             short; shrinks to 0 and lets the normal scroll take over once
             content overflows the viewport. -->
        <div class="scm-fill" />

        <!-- Delete -->
        <div v-if="canDelete" class="scm-danger-group">
          <button
            type="button"
            class="scm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="scm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.serviceCategory.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.scm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
.scm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.scm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.scm-nav-back:active {
  opacity: 0.6;
}

.scm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.scm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.scm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.scm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

/* ── Loading / error ─────────────────────────────────────────────────────── */
.scm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.scm-state--error {
  color: var(--color-money-negative);
}

/* ── Scrollable content ──────────────────────────────────────────────────── */
.scm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Category header ─────────────────────────────────────────────────────── */
.scm-cat-header {
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 1.3rem 1.25rem 1.2rem;
}

.scm-cat-name {
  font-size: 1.9rem;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.scm-cat-desc {
  font-size: 1.02rem;
  line-height: 1.45;
  color: var(--color-tufte-secondary);
  margin-top: 0.6rem;
}
.scm-cat-desc--empty {
  font-style: italic;
  color: var(--color-tufte-muted);
  opacity: 0.6;
}

/* ── Stat strip ──────────────────────────────────────────────────────────── */
.scm-stat-strip {
  display: flex;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.scm-stat-cell {
  flex: 1;
  padding: 0.75rem 0.5rem;
  text-align: center;
  border-right: 1px solid var(--color-tufte-border);
}
.scm-stat-cell:last-child {
  border-right: none;
}

.scm-stat-num {
  font-size: 1.4rem;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.scm-stat-num--pos {
  color: var(--color-money-positive);
}

.scm-stat-cap {
  font-family: var(--font-tufte-sans);
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.3rem;
}

/* ── Section label ───────────────────────────────────────────────────────── */
.scm-section-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  padding: 0 1.25rem 0.4rem;
  margin-top: 1.2rem;
}

/* ── Group card ──────────────────────────────────────────────────────────── */
.scm-group {
  background: var(--color-tufte-bg);
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
}

/* ── Service type rows ───────────────────────────────────────────────────── */
.scm-type-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 1.25rem;
  border-bottom: 1px solid #eeeee8;
}
.scm-type-row:last-child {
  border-bottom: none;
}
.scm-type-row:active {
  background: color-mix(in srgb, var(--color-tufte-border) 30%, var(--color-tufte-bg));
}

.scm-type-main {
  flex: 1;
  min-width: 0;
}
.scm-type-name {
  font-size: 1.02rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.scm-type-sub {
  font-family: var(--font-tufte-sans);
  font-size: 0.78rem;
  color: var(--color-tufte-muted);
  margin-top: 0.1rem;
}

.scm-type-price {
  flex-shrink: 0;
  font-family: var(--font-tufte-mono);
  font-size: 0.98rem;
  color: var(--color-money-positive);
  font-variant-numeric: tabular-nums;
}
.scm-type-cur {
  font-size: 0.72rem;
  color: var(--color-tufte-muted);
  margin-left: 0.1rem;
}

.scm-type-chev {
  flex-shrink: 0;
  color: var(--color-tufte-border);
}

.scm-type-empty {
  padding: 0.75rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.85rem;
  color: var(--color-tufte-muted);
  font-style: italic;
}

.scm-fill {
  flex: 1;
}

/* ── Danger group ────────────────────────────────────────────────────────── */
.scm-danger-group {
  margin-top: 1.4rem;
}

.scm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.scm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.scm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.scm-scroll-end {
  height: 2.5rem;
}
</style>
